import React, { useCallback, useMemo, useState } from 'react';
import { Column, Row, Title } from '@ydistri/ds';
import { EntityListTypeSupported } from '../../../entityListsLib';
import { useCurrentEntityListState } from '../../../hooks/useCurrentEntityListState';
import { toEnumValue } from '../../../../../../lib/utils/utils';
import { StatisticsItem } from '../../../../common/administrationItemsTypes';
import EntityListParsedItemsTable from '../EntityListParsedItemsTable';
import ParsedItemStats, {
  ParsedItemStatsType,
} from '../../../../../CalculationDetail/Redistribution/parts/import/components/steps/1_import/parsing_result_overview/ParsedItemStats';

interface EntityListImportParsingResultOverviewProps {
  entityListType: EntityListTypeSupported;
}

const EntityListImportParsingResultOverview: React.FC<
  EntityListImportParsingResultOverviewProps
> = ({ entityListType }) => {
  const entityListState = useCurrentEntityListState(entityListType);
  const items = entityListState?.parsedImportedEntityListItems;

  const errorsCount = useMemo(() => {
    if (items) {
      return items.reduce((acc, item) => {
        return acc + (item.isError ? 1 : 0);
      }, 0);
    } else {
      return 0;
    }
  }, [items]);

  // we need to distinguish between unselected and null (cleared) filter,
  // so we use undefined for unselected and null for cleared
  // if filter is unselected, and there are errors, we will show only errors by default
  const [userSelectedItemType, setUserSelectedItemType] = useState<
    ParsedItemStatsType | null | undefined
  >();

  /**
   * Handles the selection of a statistics item and filters the table based on the selected item type.
   * If the item is null, it resets the filter.
   * @param item - The selected statistics item or null to reset the filter.
   */
  const statsItemSelectedHandler = useCallback((item: StatisticsItem | null) => {
    if (item !== null) {
      const value = toEnumValue(ParsedItemStatsType, item.label);
      if (value) {
        setUserSelectedItemType(value);
        return;
      }
    }
    setUserSelectedItemType(null);
  }, []);

  const computeFilteredItems = useCallback(
    (filterType: ParsedItemStatsType | null | undefined) => {
      if (items && filterType) {
        switch (filterType) {
          case ParsedItemStatsType.VALID:
            return items.filter(item => !item.isError);
          case ParsedItemStatsType.ERROR:
            return items.filter(item => item.isError);
          case ParsedItemStatsType.TOTAL:
          default:
            return items;
        }
      }
      return items;
    },
    [items],
  );

  const { filteredItemType, itemsFiltered } = useMemo(() => {
    let resolvedFilterType: ParsedItemStatsType | undefined;

    if (userSelectedItemType !== undefined) {
      const hasItemsForSelectedType = (() => {
        switch (userSelectedItemType) {
          case ParsedItemStatsType.VALID:
            return items?.some(item => !item.isError) ?? false;
          case ParsedItemStatsType.ERROR:
            return items?.some(item => item.isError) ?? false;
          case ParsedItemStatsType.TOTAL:
          case null:
            return (items?.length ?? 0) > 0;
        }
      })();

      if (!hasItemsForSelectedType) {
        resolvedFilterType = errorsCount > 0 ? ParsedItemStatsType.ERROR : undefined;
      } else {
        resolvedFilterType = userSelectedItemType ?? undefined;
      }
    } else if (errorsCount > 0) {
      resolvedFilterType = ParsedItemStatsType.ERROR;
    } else {
      resolvedFilterType = undefined;
    }

    return {
      filteredItemType: resolvedFilterType,
      itemsFiltered: computeFilteredItems(resolvedFilterType),
    };
  }, [userSelectedItemType, errorsCount, computeFilteredItems, items]);

  if ((items?.length ?? 0) > 0) {
    return (
      <Column $gap="10px" $flexGrow={1}>
        <Row $alignItems="center" $gap="4rem">
          {items && (
            <ParsedItemStats
              items={items}
              errorsCount={errorsCount}
              onItemSelected={statsItemSelectedHandler}
              selectedItemType={filteredItemType}
            />
          )}
          {errorsCount > 0 && (
            <Title $size="large" $type="danger">
              Only valid items will be processed
            </Title>
          )}
        </Row>
        <EntityListParsedItemsTable entityListType={entityListType} items={itemsFiltered} />
      </Column>
    );
  }
};

export default EntityListImportParsingResultOverview;
